import {
  AddToCartButton,
  Asset,
  MatchingProduct,
  PriceContainer,
  SolutionComponent,
} from '../Types/MatchingProducts.ts';
import { setAddToCartButton, setPricePDP, refreshPlacements } from '../../FitAssistantShared/Functions.js';
import { createRoot } from 'react-dom/client';
import React from 'react';
import FittingInstructionMovie from '../../FitAssistantShared/FittingInstructionMovie.jsx';

/**
 * Lightweight error boundary for non-critical sub-widgets.
 * Logs to console.error (Sentry Logs budget) instead of captureException (Error budget).
 * A crash here just hides the video thumbnails — the FitAssistant wizard continues working.
 */
class FittingInstructionMovieBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[FittingInstructionMovie] render error', error, info.componentStack);
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}
import { FitAssistantProductsResponse } from '../Types/FitAssistantGraphQLTypes.ts';
import { initMobileSlider } from '../../../ProductDetail_MobileSlider.js';

export const setAddToCartBtnForDBG = (pli: HTMLDivElement, matchingProduct: MatchingProduct | undefined) => {
  if (!pli || !matchingProduct || !window._THULEDATA?._buyersGuide) return;
  let productIdsElement: HTMLInputElement = pli.querySelector(
    '.product-add-to-cart .product-ids-for-solution'
  ) as HTMLInputElement;
  if (productIdsElement) {
    productIdsElement.value = matchingProduct.ProductIds.join(',');
  }
};

export const UpdateFitAssistantPriceAndATCForPDP = (
  solutionPriceContainer: PriceContainer,
  addToCartButton: AddToCartButton
) => {
  setAddToCartButton(addToCartButton, true);
  setPricePDP(solutionPriceContainer);
  refreshPlacements(solutionPriceContainer);
};

export const UpdateFitAssistantPriceAndATCForPDPExtranet = (solutionComponents: SolutionComponent[]) => {
  if (!solutionComponents?.length) return;

  let productBuyArea: HTMLDivElement = document.querySelector('#productBuyArea') as HTMLDivElement;
  let priceElement: HTMLDivElement = productBuyArea.querySelector('.price') as HTMLDivElement;
  let addToCartElement: HTMLDivElement = productBuyArea.querySelector('.add-to-your-cart') as HTMLDivElement;
  let spinner: HTMLDivElement = productBuyArea.querySelector('.loading-spinner') as HTMLDivElement;

  spinner?.classList.remove('d-none');
  priceElement?.classList.add('d-none');
  addToCartElement?.classList.add('d-none');

  import('./SolutionAddToCart')
    .then((module) => module.getSolutionPriceFromM3(solutionComponents))
    .then((isCurrentSolution) => {
      if (!isCurrentSolution) return;

      spinner?.classList.add('d-none');
      priceElement.classList.remove('d-none');
      addToCartElement?.classList.remove('d-none');
    });
};

type SolutionInstructionalVideo = {
  mediaType: 'video';
  id: string;
  videoProvider: 'cloudflare' | 'youtube' | 'direct';
  source: string;
  posterUrl?: string;
  title?: string;
  altText?: string;
  isInstructional: true;
  footType?: string;
  generation?: string;
};

const toInstructionalVideo = (movie: Asset): SolutionInstructionalVideo | null => {
  const fields = movie as Asset & {
    CloudflareMovieId?: string;
    IsDirectVideo?: boolean;
    HighResThumbnailUrl?: string;
  };
  const cloudflareId = fields.CloudflareMovieId || '';
  const source = cloudflareId || movie.AssetURL || '';
  if (!source) return null;

  const videoProvider = movie.IsYoutube ? 'youtube' : fields.IsDirectVideo ? 'direct' : 'cloudflare';
  const posterUrl =
    fields.HighResThumbnailUrl || movie.ThumbnailUrl?.replace('/default.jpg', '/maxresdefault.jpg') || undefined;

  return {
    mediaType: 'video',
    id: movie.AssetId || source,
    videoProvider,
    source,
    posterUrl,
    title: movie.VideoTitle,
    altText: movie.AssetDescription || undefined,
    isInstructional: true,
    footType: movie.FootType || undefined,
    generation: movie.Generation || undefined,
  };
};

const collectInstructionalVideos = (solutionComponents: SolutionComponent[] | undefined): SolutionInstructionalVideo[] => {
  const videos: SolutionInstructionalVideo[] = [];
  const seen = new Set<string>();
  solutionComponents?.forEach((component) => {
    component.FittingInstructionMovies?.forEach((movie) => {
      const video = toInstructionalVideo(movie);
      if (!video || seen.has(video.id)) return;
      seen.add(video.id);
      videos.push(video);
    });
  });
  return videos;
};

export const setImageBasedOnRoofRack = (response: FitAssistantProductsResponse) => {
  if (!response?.MatchingProducts || response.MatchingProducts.length === 0) return;

  const footGeneration = response.MatchingProducts[0].SolutionFootGeneration;
  const footType = response.MatchingProducts[0].SolutionFootType;
  const instructionalVideos = collectInstructionalVideos(response.SolutionComponents);

  let containerDiv = document.querySelector('.pdp__mainImages[data-isroofrack="True"]') as HTMLElement;
  if (!containerDiv || !footGeneration || !footType) return;

  let imageWrappers = containerDiv.querySelectorAll('.pdp__mainImageWrapper') as NodeListOf<HTMLElement>;
  imageWrappers.forEach((imageWrapper) => {
    if (imageWrapper.dataset.generation === footGeneration && imageWrapper.dataset.foottype === footType) {
      imageWrapper.classList.remove('d-none');
      imageWrapper.classList.add('splide__slide');
    } else {
      imageWrapper.classList.add('d-none');
      imageWrapper.classList.remove('splide__slide');
    }
  });
  initMobileSlider();

  window.dispatchEvent(
    new CustomEvent('roofRackCarSelected', {
      detail: { footType, footGeneration, instructionalVideos },
    })
  );
};

// Server-rendered manuals videos, captured before the first car selection
// replaces them with the solution's fitting videos.
let initialManualsVideosHtml: string | null = null;

const rememberInitialManualsVideos = (container: HTMLElement) => {
  if (initialManualsVideosHtml !== null) return;
  initialManualsVideosHtml = container.innerHTML;
};

export const resetRoofRackMediaFilter = () => {
  const isRoofRackPage = !!document.querySelector('.pdp__mainImages[data-isroofrack="True"]');
  const instructionMovieContainer = document.getElementById('image-navigation');
  if (isRoofRackPage && instructionMovieContainer && initialManualsVideosHtml !== null) {
    instructionMovieContainer.innerHTML = initialManualsVideosHtml;
  }
  window.dispatchEvent(new CustomEvent('roofRackCarReset'));
};

export const handleDocuments = (solutionComponents: SolutionComponent[]) => {
  const documentContainerElement = document.querySelector('#documentsContainer') as HTMLElement;
  const instructionMovieContainer = document.getElementById('image-navigation') as HTMLElement;
  if (!documentContainerElement || !solutionComponents) return;

  const isV2 = documentContainerElement.classList.contains('pdp__instructionDocs');

  documentContainerElement.innerHTML = '';
  if (instructionMovieContainer) {
    if (isV2) {
      rememberInitialManualsVideos(instructionMovieContainer);
      instructionMovieContainer.innerHTML = '';
    } else {
      instructionMovieContainer.querySelectorAll('.fittingInstructionMovie').forEach((movie) => {
        movie.remove();
      });
    }
  }
  let assetIds: string[] = [];

  solutionComponents.forEach((c) => {
    if (c.Informationdoc?.length > 0) {
      addInstructions(c.Informationdoc, c.InformationDocumentsLinkText, assetIds, isV2);
    }

    if (c.FittingInstruction?.length > 0) {
      addInstructions(c.FittingInstruction, c.FittingInstructionLinkText, assetIds, isV2);
    }

    if (c.CompatibilityDocuments?.length > 0) {
      addInstructions(c.CompatibilityDocuments, c.CompatibilityDocumentsLinkText, assetIds, isV2);
    }

    if (c.FittingInstructionMovies?.length > 0) {
      if (isV2) {
        renderFittingInstructionMoviesV2(c.FittingInstructionMovies, instructionMovieContainer);
      } else {
        renderFittingInstructionMovies(c.FittingInstructionMovies, instructionMovieContainer);
      }
    }
  });
};

const addInstructions = (instructionsList: Asset[], linkText: string, assetIds: string[], isV2: boolean) => {
  const documentContainerElement = document.querySelector('#documentsContainer');
  if (!documentContainerElement) return;
  instructionsList.forEach((d) => {
    if (assetIds.indexOf(d.AssetId) === -1) {
      assetIds.push(d.AssetId);
      if (isV2) {
        documentContainerElement.innerHTML += `<li><i class="le-icon-file-text"></i><a target="_blank" class="tag-manager-user-manual paragraph--s" href="${d.AssetURL}" data-assettype="${d.FileEnding}" data-component-label="Open user manual">${linkText}</a></li>`;
      } else {
        const icon = d.IconCssClass ? `<i class="${d.IconCssClass}"></i>` : '';
        documentContainerElement.innerHTML += `<li class="downloadable-asset">${icon} <a target="_blank" class="tag-manager-user-manual paragraph--s text-decoration-underline" href="${d.AssetURL}" data-assettype="${d.FileEnding}" data-component-label="Open user manual">${linkText}</a></li>`;
      }
    }
  });
};

const renderFittingInstructionMoviesV2 = (movies: Asset[], container: HTMLElement) => {
  if (!container) return;
  let slider = container.querySelector('.pdp__featuresSlider') as HTMLElement;
  if (!slider) {
    slider = document.createElement('ul');
    slider.className = 'pdp__featuresSlider pdp__featuresSlider--manuals scrollBar--3';
    container.appendChild(slider);
  }

  movies.forEach((movie) => {
    const m = movie as any;
    const cloudflareId = m.CloudflareMovieId || '';
    const youtubeUrl = movie.IsYoutube ? movie.AssetURL : '';
    const directUrl = m.IsDirectVideo ? movie.AssetURL : '';
    const posterImage =
      m.HighResThumbnailUrl || movie.ThumbnailUrl?.replace('/default.jpg', '/maxresdefault.jpg') || '';

    const li = document.createElement('li');
    li.className = 'pdp__featureCard';
    const title = movie.VideoTitle?.trim();
    li.innerHTML =
      `<div class="pdp__featureCardImage">` +
      `<thule-video-player cloudflare-id="${cloudflareId}" youtube-url="${youtubeUrl}" direct-url="${directUrl}" poster-image="${posterImage}" compact-player="true" click-fullscreen="true"></thule-video-player>` +
      `</div>` +
      (title ? `<h3 class="heading5">${title}</h3>` : '');
    slider.appendChild(li);
  });
};

const renderFittingInstructionMovies = (movies: Asset[], instructionMovieContainer: HTMLElement) => {
  const existingRoot = (instructionMovieContainer as any)._reactRoot;
  const root = existingRoot || createRoot(instructionMovieContainer);

  if (!existingRoot) {
    (instructionMovieContainer as any)._reactRoot = root;
  }

  root.render(
    <FittingInstructionMovieBoundary>
      <>
        {movies.map((movie: Asset, index: number) => (
          <FittingInstructionMovie key={index} component={movie} index={index} />
        ))}
      </>
    </FittingInstructionMovieBoundary>
  );
};
